/**
 * QC charts — Batting tab.
 *   Batter cards · Batter vs bowler matchups · False shots & control ·
 *   Scoring vs pace and spin · Footwork
 */
import { useMemo, useState } from "react";
import {
  CONN_GROUPS, FOOT_ORDER, OUTCOME_LEGEND, connGroup, inOrder, isAerial, isControlled,
  isUncontrolled, niceWicket, pct0, sr, styleResolver,
} from "./qcData";
import { Bar, Card, Empty, Legend, MiniWagon, Toggle } from "./qcUi";
import { useTip } from "./useTip";

// Per-batter totals, in order of arrival at the crease.
function batterRows(balls) {
  const m = new Map();
  for (const b of inOrder(balls)) {
    for (const [id, name] of [[b.strikerId, b.striker], [b.nonStrikerId, b.nonStriker]]) {
      if (id && !m.has(id)) m.set(id, { id, name, innings: b.innings, team: b.teamName, runs: 0, balls: 0, dots: 0, fours: 0, sixes: 0, lefty: false, out: null, shots: [] });
    }
    const r = m.get(b.strikerId);
    if (r) {
      r.lefty = r.lefty || b.lefty;
      r.runs += b.offBat;
      if (b.faced) {
        r.balls += 1;
        if (b.offBat === 0) r.dots += 1;
        r.shots.push(b);
      }
      if (b.offBat === 4) r.fours += 1;
      if (b.offBat === 6) r.sixes += 1;
    }
    if (b.wicket) {
      const out = m.get(b.dismissedId || b.strikerId);
      if (out) out.out = b;
    }
  }
  return [...m.values()];
}

// ── 6. Batter cards ──────────────────────────────────────────────────
function BatterCards({ balls }) {
  const rows = useMemo(() => batterRows(balls), [balls]);
  if (!rows.length) return <Card wide title="Batters"><Empty>No balls in this selection.</Empty></Card>;
  return (
    <Card wide title="Batters" subtitle="Every batter so far, in batting order · wagon wheel in the batter's own hand · ✕ = the dismissal">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-6 gap-3">
        {rows.map((r) => (
          <div key={r.id} className={`rounded-lg border p-2.5 ${r.out ? "border-border-default" : "border-emerald-500/50"}`}>
            <div className="flex items-baseline justify-between gap-1 min-w-0">
              <span className="text-[12px] font-semibold text-text-primary truncate" title={r.name}>{r.name || "?"}</span>
              <span className="text-[9px] uppercase text-text-secondary shrink-0">{r.lefty ? "LHB" : "RHB"} · Inns {r.innings}</span>
            </div>
            <div className="text-lg font-semibold text-text-primary tabular-nums leading-tight">
              {r.runs}{!r.out && "*"} <span className="text-[11px] text-text-secondary font-normal">({r.balls})</span>
            </div>
            <div className="text-[10px] text-text-secondary tabular-nums">SR {sr(r.runs, r.balls)} · {r.fours}×4 · {r.sixes}×6 · dots {pct0(r.dots, r.balls)}</div>
            <div className="text-[10px] truncate mt-0.5 text-text-secondary" title={r.out ? niceWicket(r.out.wicket) : "not out"}>
              {r.out ? `${niceWicket(r.out.wicket)}${r.out.caughtBy ? ` c ${r.out.caughtBy}` : ""} b ${r.out.bowler}` : <span className="text-emerald-400">not out</span>}
            </div>
            <div className="mt-2 max-w-[150px] mx-auto"><MiniWagon balls={r.shots} lh={r.lefty} /></div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-center"><Legend items={OUTCOME_LEGEND} /></div>
    </Card>
  );
}

// ── 7. Batter vs bowler matchups ─────────────────────────────────────
function Matchups({ balls }) {
  const tip = useTip();
  const inningsList = useMemo(() => [...new Set(balls.map((b) => b.innings).filter((x) => x != null))].sort(), [balls]);
  const [inn, setInn] = useState(null);
  const current = inn ?? inningsList[inningsList.length - 1];
  const data = useMemo(() => {
    const bs = balls.filter((b) => b.innings === current && b.faced);
    const batters = batterRows(bs).filter((r) => r.balls > 0);
    const bowlers = [];
    const cells = new Map();
    for (const b of inOrder(bs)) {
      if (b.bowlerId && !bowlers.find((x) => x.id === b.bowlerId)) bowlers.push({ id: b.bowlerId, name: b.bowler });
      const k = `${b.strikerId}|${b.bowlerId}`;
      const c = cells.get(k) || { runs: 0, balls: 0, dots: 0, fours: 0, sixes: 0, outs: 0 };
      c.runs += b.offBat;
      c.balls += 1;
      if (b.offBat === 0) c.dots += 1;
      if (b.offBat === 4) c.fours += 1;
      if (b.offBat === 6) c.sixes += 1;
      if (b.wicket && (b.dismissedId || b.strikerId) === b.strikerId) c.outs += 1;
      cells.set(k, c);
    }
    const max = Math.max(1, ...[...cells.values()].map((c) => c.runs));
    return { batters, bowlers, cells, max };
  }, [balls, current]);

  if (!data.batters.length) return <Card wide title="Batter vs bowler"><Empty>No balls in this selection.</Empty></Card>;
  return (
    <Card wide title="Batter vs bowler" subtitle="Runs (balls) for each matchup · shade = runs scored · ✕ = dismissed by that bowler"
      controls={inningsList.length > 1 && (
        <Toggle label="Innings" value={current} onChange={setInn} options={inningsList.map((i) => ({ key: i, label: `Inns ${i}` }))} />
      )}>
      <div data-tip-root className="relative overflow-x-auto">
        <table className="text-[11px] border-separate" style={{ borderSpacing: 2 }}>
          <thead>
            <tr>
              <th className="text-left text-[9px] uppercase tracking-wider text-text-secondary font-normal pr-2">Batter ↓ · Bowler →</th>
              {data.bowlers.map((bw) => <th key={bw.id} className="text-[10px] text-text-secondary font-normal px-1 pb-1 whitespace-nowrap">{bw.name}</th>)}
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {data.batters.map((bt) => (
              <tr key={bt.id}>
                <th className="text-left text-text-primary font-normal pr-2 whitespace-nowrap">{bt.name} <span className="text-text-secondary">{bt.runs}({bt.balls})</span></th>
                {data.bowlers.map((bw) => {
                  const c = data.cells.get(`${bt.id}|${bw.id}`);
                  if (!c) return <td key={bw.id} className="h-9 min-w-[64px] rounded-[4px]" style={{ background: "var(--qc-grid)" }} />;
                  const a = 0.12 + 0.7 * (c.runs / data.max);
                  return (
                    <td key={bw.id}
                      className={`h-9 min-w-[64px] text-center rounded-[4px] ${a > 0.55 ? "text-white" : "text-text-primary"} ${c.outs ? "ring-2 ring-inset ring-white/80" : ""}`}
                      style={{ background: `rgba(var(--qc-heat), ${a})` }}
                      onMouseMove={(ev) => tip.show(ev, [`${bt.name} v ${bw.name}`, `${c.runs} runs off ${c.balls} · SR ${sr(c.runs, c.balls)}`, `${c.dots} dots · ${c.fours}×4 · ${c.sixes}×6${c.outs ? " · OUT" : ""}`])}
                      onMouseLeave={tip.hide}>
                      <span className="font-semibold">{c.runs}</span><span className="opacity-80">({c.balls})</span>{c.outs ? " ✕" : ""}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {tip.node}
      </div>
    </Card>
  );
}

// ── 8. False shots & control ─────────────────────────────────────────
const CONN_COLOR = { good: "#38bdf8", normal: "#64748b", false: "#f97316", body: "#facc15", left: "rgba(148,163,184,0.45)" };

function FalseShots({ balls }) {
  const [by, setBy] = useState("batter");
  const tip = useTip();
  const rows = useMemo(() => {
    const m = new Map();
    for (const b of balls) {
      if (!b.faced) continue;
      const g = connGroup(b);
      const id = by === "batter" ? b.strikerId : b.bowlerId;
      const name = by === "batter" ? b.striker : b.bowler;
      if (!id) continue;
      const r = m.get(id) || { id, name, coded: 0, seg: { good: 0, normal: 0, false: 0, body: 0, left: 0 }, aerial: 0, ctrl: 0, unctrl: 0 };
      if (g) {
        r.coded += 1;
        r.seg[g] += 1;
      }
      if (isAerial(b)) {
        r.aerial += 1;
        if (isControlled(b)) r.ctrl += 1;
        if (isUncontrolled(b)) r.unctrl += 1;
      }
      m.set(id, r);
    }
    return [...m.values()].filter((r) => r.coded).sort((a, b) => b.coded - a.coded);
  }, [balls, by]);
  const coded = rows.reduce((t, r) => t + r.coded, 0);
  const falseAll = rows.reduce((t, r) => t + r.seg.false, 0);
  if (!rows.length) return <Card title="False shots & control"><Empty>No shot connection coded in this selection.</Empty></Card>;
  return (
    <Card title="False shots & control"
      subtitle={`${pct0(falseAll, coded)} false shots overall (edges, play & miss, mis-timed, bat-pad, gloved) · from shot connection`}
      controls={<Toggle label="Group by" value={by} onChange={setBy} options={[{ key: "batter", label: "Batters" }, { key: "bowler", label: "Bowlers" }]} />}>
      <div data-tip-root className="relative">
        <table className="w-full text-[11px]">
          <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
            <tr>
              <th className="text-left py-1 font-normal">{by === "batter" ? "Batter" : "Bowler"}</th>
              <th className="font-normal w-[42%]" />
              <th className="text-right font-normal">False</th>
              <th className="text-right font-normal" title="Aerial shots: controlled / uncontrolled">In air</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border-default">
                <td className="py-1.5 pr-2 text-text-primary whitespace-nowrap">{r.name} <span className="text-text-secondary">({r.coded})</span></td>
                <td className="pr-2">
                  <div className="flex h-2.5 rounded-[3px] overflow-hidden" style={{ gap: 1 }}
                    onMouseMove={(ev) => tip.show(ev, [r.name, ...CONN_GROUPS.map((g) => `${g.label}: ${r.seg[g.key]} (${pct0(r.seg[g.key], r.coded)})`)])}
                    onMouseLeave={tip.hide}>
                    {CONN_GROUPS.filter((g) => r.seg[g.key]).map((g) => <div key={g.key} style={{ flexGrow: r.seg[g.key], background: CONN_COLOR[g.key] }} />)}
                  </div>
                </td>
                <td className={`text-right font-semibold ${r.seg.false / r.coded >= 0.25 ? "text-amber-400" : "text-text-primary"}`}>{pct0(r.seg.false, r.coded)}</td>
                <td className="text-right text-text-secondary">{r.aerial ? `${r.ctrl}/${r.unctrl}` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {tip.node}
      </div>
      <div className="mt-2"><Legend items={CONN_GROUPS.map((g) => ({ label: g.label, color: CONN_COLOR[g.key] }))} /></div>
      <p className="mt-1 text-[10px] text-text-secondary">"In air" = aerial shots, controlled / uncontrolled. False % of 25% or more is highlighted.</p>
    </Card>
  );
}

// ── 9. Scoring vs pace and spin ──────────────────────────────────────
function PaceVsSpin({ balls }) {
  const styleOf = useMemo(() => styleResolver(balls), [balls]);
  const groups = useMemo(() => ["pace", "spin"].map((k) => {
    const bs = balls.filter((b) => b.faced && styleOf(b) === k);
    const runs = bs.reduce((t, b) => t + b.offBat, 0);
    return {
      key: k, label: k === "pace" ? "Pace" : "Spin", balls: bs, runs,
      n: bs.length,
      dots: bs.filter((b) => b.offBat === 0).length,
      fours: bs.filter((b) => b.offBat === 4).length,
      sixes: bs.filter((b) => b.offBat === 6).length,
      outs: bs.filter((b) => b.wicket).length,
    };
  }), [balls, styleOf]);
  const unknown = balls.filter((b) => b.faced && styleOf(b) === "unknown").length;
  if (!groups.some((g) => g.n)) return <Card title="Scoring vs pace and spin"><Empty>No delivery types coded, so pace and spin can't be told apart.</Empty></Card>;
  return (
    <Card title="Scoring vs pace and spin" subtitle={`Pace or spin from the coded delivery type (else the bowler's other balls)${unknown ? ` · ${unknown} balls unclassified` : ""} · right-hander's view`}>
      <div className="grid grid-cols-2 gap-3">
        {groups.map((g) => (
          <div key={g.key} className="rounded-lg border border-border-default p-2.5">
            <div className="flex items-baseline justify-between">
              <span className="text-[12px] font-semibold text-text-primary">{g.label}</span>
              <span className="text-[10px] text-text-secondary tabular-nums">{g.n} balls</span>
            </div>
            <div className="text-lg font-semibold text-text-primary tabular-nums leading-tight">{g.runs} <span className="text-[11px] text-text-secondary font-normal">SR {sr(g.runs, g.n)}</span></div>
            <div className="text-[10px] text-text-secondary tabular-nums">dots {pct0(g.dots, g.n)} · {g.fours}×4 · {g.sixes}×6 · {g.outs} out</div>
            <div className="mt-2 max-w-[190px] mx-auto"><MiniWagon balls={g.balls} /></div>
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-center"><Legend items={OUTCOME_LEGEND} /></div>
    </Card>
  );
}

// ── 10. Footwork ─────────────────────────────────────────────────────
function Footwork({ balls }) {
  const rows = useMemo(() => {
    const m = new Map();
    for (const b of balls) {
      if (!b.faced || !b.foot) continue;
      const r = m.get(b.foot) || { foot: b.foot, n: 0, runs: 0, dots: 0, bound: 0, outs: 0, coded: 0, false: 0 };
      r.n += 1;
      r.runs += b.offBat;
      if (b.offBat === 0) r.dots += 1;
      if (b.offBat === 4 || b.offBat === 6) r.bound += 1;
      if (b.wicket) r.outs += 1;
      const g = connGroup(b);
      if (g) { r.coded += 1; if (g === "false") r.false += 1; }
      m.set(b.foot, r);
    }
    const rank = (f) => { const i = FOOT_ORDER.findIndex((x) => x.toLowerCase() === f.toLowerCase()); return i < 0 ? 99 : i; };
    return [...m.values()].sort((a, b) => b.n - a.n || rank(a.foot) - rank(b.foot));
  }, [balls]);
  if (!rows.length) return <Card title="Footwork"><Empty>No foot movement coded in this selection.</Empty></Card>;
  const total = rows.reduce((t, r) => t + r.n, 0);
  const max = Math.max(...rows.map((r) => r.n));
  return (
    <Card title="Footwork" subtitle={`${total} balls with foot movement coded · what each movement produced`}>
      <table className="w-full text-[11px]">
        <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
          <tr>
            <th className="text-left py-1 font-normal">Movement</th><th className="font-normal w-[26%]" />
            <th className="text-right font-normal">Runs</th><th className="text-right font-normal">SR</th>
            <th className="text-right font-normal">Dot %</th><th className="text-right font-normal">4s+6s</th>
            <th className="text-right font-normal">False</th><th className="text-right font-normal">Out</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {rows.map((r) => (
            <tr key={r.foot} className="border-t border-border-default">
              <td className="py-1.5 pr-2 text-text-primary whitespace-nowrap">{r.foot}</td>
              <td className="pr-2"><Bar value={r.n} max={max} label={`${r.n} · ${pct0(r.n, total)}`} /></td>
              <td className="text-right text-text-primary font-semibold">{r.runs}</td>
              <td className="text-right text-text-secondary">{sr(r.runs, r.n)}</td>
              <td className="text-right text-text-secondary">{pct0(r.dots, r.n)}</td>
              <td className="text-right text-text-secondary">{r.bound}</td>
              <td className="text-right text-text-secondary">{r.coded ? pct0(r.false, r.coded) : "—"}</td>
              <td className="text-right text-text-primary">{r.outs || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export default function BattingCharts({ balls }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <BatterCards balls={balls} />
      <Matchups balls={balls} />
      <FalseShots balls={balls} />
      <PaceVsSpin balls={balls} />
      <Footwork balls={balls} />
    </div>
  );
}
